import { Button, ButtonVariant } from '@ampcore/adel-react'
import { Modal } from '@ampcore/adel-react'
import { Notification } from '@ampcore/adel-react'
import { NotificationType } from '@ampcore/adel-react'
import { Toggle } from '@ampcore/adel-react'
import { DataPrivacyManager } from '@ampcore/data-privacy-manager'
import React, { useCallback, useEffect, useState } from 'react'

export type DataPrivacyPreferenceCenterProps = {
  testId?: string
}

export const DataPrivacyPreferenceCenter = ({
  testId
}: DataPrivacyPreferenceCenterProps) => {
  const dataPrivacyManager = DataPrivacyManager.getInstance()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)

  // Store the user's selection for the toggle
  const [consentOptIn, setConsentOptIn] = useState(false)
  const [consentToggleButton, setConsentToggleButton] = useState(false)

  useEffect(() => {
    // On component mount, check if the user has a saved preference using DataPrivacyManager
    const optedIn = dataPrivacyManager.getConsentState()
    setConsentToggleButton(optedIn)
    setConsentOptIn(optedIn)
  }, [dataPrivacyManager])

  const handleConfirmChoices = () => {
    handleCloseModal()
    if (consentToggleButton) {
      setConsentOptIn(true)
      dataPrivacyManager.optIn(true)
    } else {
      setConsentOptIn(false)
      dataPrivacyManager.optOut(true)
    }
  }

  // Dispatch OneTrust event when toggle changes
  const handleToggle = useCallback(() => {
    setConsentToggleButton(prev => !prev)
  }, [])

  return (
    <div className="DataPrivacyPreferenceCenter" data-testid={testId}>
      <Button
        onClick={handleOpenModal}
        testId={testId ? `${testId}-open-btn` : undefined}
        data-analytics="do-not-sell-or-share-my-personal-data-btn"
      >
        Do not sell or share my personal data
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        className="DataPrivacyPreferenceCenter"
        size="full"
        header={<h1>Preference Center</h1>}
        footer={
          <div className="ButtonRow">
            <Button
              testId={testId ? `${testId}-confirm-btn` : undefined}
              variant={ButtonVariant.PRIMARY}
              className="is-destructive"
              data-analytics="confirm-my-choices-btn"
              onClick={handleConfirmChoices}
            >
              Confirm My Choices
            </Button>
          </div>
        }
        showCloseButton
        ariaLabel="Preference Center"
      >
        {!consentOptIn && (
          <Notification
            testId={testId ? `${testId}-notification` : undefined}
            type={NotificationType.CONFIRM}
            className="u-marginBottom20"
            message="Your Opt Out Preference Signal is Honored"
            dismissable={false}
          />
        )}
        <div>
          <p className="u-marginBottom20">
            When you visit our website, we store cookies on your browser to
            collect information. We use cookies to share information with our
            advertising partners to assist in our marketing efforts.
            Specifically, we share your website activity with marketing
            providers to market products to you on other
            websites—cross-contextual behavioral advertising. We also use other
            cookies that are strictly necessary to ensure the proper functioning
            of our website and preserve your preferences. Those cookies are not
            used to market to you and cannot be turned off or limited. Further
            information is available in our{' '}
            <a
              href="/privacy-security-fraud/internet-privacy-statement"
              data-analytics="cookie-notice-link"
              tabIndex={0}
            >
              Cookie Notice
            </a>{' '}
            .
          </p>
          <p className="u-marginBottom20">
            You have a right to request we not share your data for
            cross-contextual behavioral advertising. Use the below toggle slider
            to confirm your preferences. Toggle color Gray means you have opted
            out of data sharing and we won't sell or share your data with third
            party agencies. You can change your preferences and confirm your
            choices anytime.
          </p>
        </div>

        <div className="Type-4 u-textBold u-marginBottom20">
          Manage consent preferences
        </div>
        <div className="Accordion">
          <details className="Accordion-item">
            <summary className="Accordion-toggle">
              <b>Share or sale of personal data</b>
              <div className="u-flexExpandLeft">
                <Toggle
                  id="opt-out-consent"
                  checked={consentToggleButton}
                  onChange={handleToggle}
                  testId={testId ? `${testId}-toggle` : undefined}
                >
                  Share or sale of personal data toggle
                </Toggle>
              </div>
            </summary>
            <div className="Accordion-content Content u-bgColorStone">
              <p>
                Under the CPRA, you have the right to opt out of the sale or
                sharing of your personal information to third-parties. These
                cookies collect information for analytics to personalize your
                experience with targeted ads. You may exercise your right to opt
                out of the sale or sharing of personal information by using this
                toggle switch. If you opt out, we will not be able to offer you
                personalized ads and will not hand over your personal
                information to any third-parties. If you have enabled privacy
                controls on your browser (such as a plugin), we have to take
                that as a valid request to opt out. Therefore we would not be
                able to track your activity through the web. This may affect our
                ability to personalize ads according to your preferences.
              </p>
              <hr />
              <p className="u-paddingBottom8">
                <b>Targeting Cookies</b>
              </p>
              <p>
                These cookies may be set through our site by our advertising
                partners. They may be used by those companies to build a profile
                of your interests and show you relevant adverts on other sites.
                They do not store directly personal information, but are based
                on uniquely identifying your browser and internet device. If you
                do not allow these cookies, you will experience less targeted
                advertising.
              </p>
              <hr />
              <p className="u-paddingBottom8">
                <b>Functional Cookies</b>
              </p>
              <p>
                These cookies enable the website to provide enhanced
                functionality and personalization. They may be set by us or by
                third party providers whose services we have added to our pages.
                If you do not allow these cookies then some or all of these
                services may not function properly.
              </p>
            </div>
          </details>
        </div>
      </Modal>
    </div>
  )
}
